import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from '../components/Button';
import Callout from '../components/Callout';
import Field from '../components/Field';
import Wordmark from '../components/Wordmark';
import { RENDER_DISCLAIMER } from '../data/style-library';
import { api, ApiError, type SharePublicView } from '../lib/api';
import { cx } from '../lib/cx';

/**
 * The committee's page. No account, no login, no app.
 *
 * The people opening this are on phones, often older, frequently in a car park
 * after a meeting. So: one screen, big targets, five buttons per concept, a
 * name, and done. It does not use AppShell because it is not part of the
 * signed-in application and should not offer navigation into it.
 */
export default function Share() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SharePublicView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const [scores, setScores] = useState<Record<string, number>>({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [large, setLarge] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .getSharedPage(token)
      .then((d) => !cancelled && setData(d))
      .catch(
        (e) =>
          !cancelled &&
          setError(
            e instanceof ApiError && e.status === 404
              ? 'This link has expired or been turned off. Ask whoever sent it for a fresh one.'
              : 'We could not open this just now. Try again in a moment.',
          ),
      );
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async () => {
    if (!token) return;
    setSending(true);
    setError(null);
    try {
      await api.submitVote(token, {
        name: name.trim(),
        email: email.trim() || undefined,
        scores,
        comment: comment.trim() || undefined,
      });
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not send. Try again.');
    } finally {
      setSending(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <div className="flex min-h-dvh flex-col bg-white">
      <header className="border-b border-grey-200 px-gutter py-5">
        <div className="mx-auto w-full max-w-wrap text-lg">
          <Wordmark />
        </div>
      </header>
      <main className="mx-auto w-full max-w-wrap flex-1 px-gutter py-10">{children}</main>
      <footer className="mt-auto border-t border-grey-200 bg-grey-050 px-gutter py-8">
        <p className="t-meta measure mx-auto w-full max-w-wrap text-grey-600">
          {RENDER_DISCLAIMER}
        </p>
      </footer>
    </div>
  );

  if (error && !data) {
    return shell(
      <>
        <h1 className="t-h1 text-ink">This link is closed</h1>
        <div className="mt-6 max-w-xl">
          <Callout tone="caution" title="Expired or turned off">
            {error}
          </Callout>
        </div>
      </>,
    );
  }

  if (!data) return shell(<p className="t-body text-grey-600">Opening…</p>);

  const scored = Object.keys(scores).length;
  const canSend = name.trim().length > 1 && scored > 0;

  return shell(
    <>
      <p className="t-eyebrow text-green-600">
        {data.verticalLabel ?? 'A building'}
        {data.sizeSf ? ` · ${data.sizeSf.toLocaleString()} sf` : ''}
      </p>
      <h1 className="t-h1 mt-3 text-ink">
        {data.projectName ?? 'Tell us what you think'}
      </h1>
      <p className="t-body-lg measure mt-4 text-grey-600">
        Someone on your committee is looking at a new building and would like your read
        on it. Nothing here is decided, and none of it is a drawing — these are ideas to
        argue with.
      </p>

      {data.brief && (
        <section className="mt-10">
          <h2 className="t-h2 text-ink">The direction so far</h2>
          <div className="mt-4 rounded-lg border-l-[3px] border-l-green-600 border-grey-200 bg-white p-6 shadow-sm">
            <p className="t-body-lg measure text-ink">{data.brief}</p>
          </div>
        </section>
      )}

      {data.concepts.length > 0 ? (
        <section className="mt-12">
          <h2 className="t-h2 text-ink">Score each one</h2>
          <p className="t-body measure mt-2 text-grey-600">
            One is “not for us”, five is “that’s it”. You do not have to score them all.
          </p>

          <ul className="mt-6 flex flex-col gap-8">
            {data.concepts.map((concept, i) => (
              <li key={concept.id} className="rounded-lg border border-grey-200 bg-white shadow-sm">
                <button
                  type="button"
                  onClick={() => setLarge(concept.url)}
                  className="block w-full"
                  aria-label={`See option ${i + 1} larger`}
                >
                  <img src={concept.url} alt={`Option ${i + 1}`} className="block w-full" />
                </button>
                <div className="p-5">
                  <p className="t-h3 text-ink">Option {i + 1}</p>
                  <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label={`Score option ${i + 1}`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        aria-pressed={scores[concept.id] === n}
                        onClick={() => setScores((s) => ({ ...s, [concept.id]: n }))}
                        className={cx(
                          'min-h-tap min-w-tap flex-1 rounded-sm border text-lg font-semibold transition-colors sm:flex-none sm:px-7',
                          scores[concept.id] === n
                            ? 'border-green-600 bg-green-050 text-green-900 shadow-[inset_0_0_0_2px_var(--color-green-600)]'
                            : 'border-grey-300 bg-white text-ink hover:bg-grey-050',
                        )}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <p className="t-meta mt-2 flex justify-between text-grey-600">
                    <span>Not for us</span>
                    <span>That’s it</span>
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <div className="mt-10">
          <Callout title="No concept images yet">
            The person who shared this has not generated the concept images yet. The
            written direction above is what there is so far.
          </Callout>
        </div>
      )}

      {data.picks.length > 0 && (
        <section className="mt-12">
          <h2 className="t-h2 text-ink">The buildings they liked</h2>
          <p className="t-body measure mt-2 text-grey-600">
            These are the photographs the direction was read from.
          </p>
          <ul className="mt-4 flex flex-wrap gap-2">
            {data.picks.map((p) => (
              <li key={p.id}>
                <button type="button" onClick={() => setLarge(p.url)} aria-label={p.caption ?? 'Photograph'}>
                  <img
                    src={p.url}
                    alt={p.caption ?? ''}
                    loading="lazy"
                    className="h-28 w-36 rounded-sm border border-grey-200 object-cover"
                  />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.allowVotes && data.concepts.length > 0 && (
        <section className="mt-14 rounded-lg border border-grey-200 bg-grey-050 p-6 sm:p-8">
          {done ? (
            <Callout tone="confirm" title="Thank you">
              Your scores are in. You can close this — and if you change your mind, open
              the link again and score it differently; the latest one replaces the last.
            </Callout>
          ) : (
            <>
              <h2 className="t-h2 text-ink">Who are you?</h2>
              <p className="t-body measure mt-2 text-grey-600">
                So the committee knows whose scores these are.
              </p>
              <div className="mt-6 grid max-w-2xl gap-6 sm:grid-cols-2">
                <Field
                  id="voter-name"
                  label="Your name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <Field
                  id="voter-email"
                  label="Email"
                  type="email"
                  hint="Optional. Only so someone can follow up with you."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <div className="sm:col-span-2">
                  <Field
                    id="voter-comment"
                    label="Anything you want to say"
                    hint="Optional, and the most useful part."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </div>
              </div>

              {error && (
                <div className="mt-6">
                  <Callout tone="caution" title="That did not send">
                    {error}
                  </Callout>
                </div>
              )}

              <div className="mt-6">
                <Button block onClick={() => void submit()} disabled={!canSend || sending}>
                  {sending
                    ? 'Sending…'
                    : scored === 0
                      ? 'Score at least one first'
                      : `Send my ${scored === 1 ? 'score' : `${scored} scores`}`}
                </Button>
              </div>
            </>
          )}
        </section>
      )}

      {!data.allowVotes && (
        <div className="mt-12">
          <Callout title="Voting is closed">
            You can still look, but scores are no longer being collected on this one.
          </Callout>
        </div>
      )}

      {large && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged image"
          className="fixed inset-0 z-50 flex flex-col bg-ink/95 p-4 sm:p-8"
          onClick={() => setLarge(null)}
        >
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setLarge(null)}
              className="min-h-tap rounded-full border border-white/35 px-6 text-[0.95rem] font-semibold text-white hover:bg-white/10"
            >
              Close
            </button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center py-4">
            <img src={large} alt="" className="max-h-full max-w-full object-contain" />
          </div>
        </div>
      )}
    </>,
  );
}
